import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Vehicle } from '../../state/types';
import { State } from '../../state/reducers';
import { getVehiclesFromDb } from '../../api/getVehiclesFromDb';
import { Filters } from '../../components/vehicles/config';

// Load initial state from sessionStorage
const loadInitialState = () => {
  const stored = sessionStorage.getItem('vehiclesNavState');
  if (!stored) return { page: 1, filters: {}, resultsPerPage: 10 };
  try {
    const parsed = JSON.parse(stored);
    return {
      page: parsed.page || 1,
      filters: parsed.filters || {},
      resultsPerPage: parsed.resultsPerPage || 10,
    };
  } catch {
    return { page: 1, filters: {}, resultsPerPage: 10 };
  }
};

export const useVehicles = () => {
  const { page: initialPage, filters: initialFilters, resultsPerPage: initialRPP } = loadInitialState();

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [loading, setLoading] = useState(true);
  
  const [page, setPage] = useState(initialPage);
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [resultsPerPage, setResultsPerPage] = useState(initialRPP);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([]);
  
  const selectedSystem = useSelector((state: State) => state.systemState.selectedSystem);

  // Function to fetch vehicles
  const fetchVehicles = async () => {
    if (!selectedSystem) return;
    setLoading(true);
    try {
      const body = {
        ...filters,
        page,
        per_page: resultsPerPage,
        system_code: selectedSystem,
        sort_direction: sortDirection,
      };
      const { vehicles: translatedVehicles, total } = await getVehiclesFromDb(body);
      setVehicles(translatedVehicles);
      setTotalVehicles(total);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      // You might want to add error state handling here
    } finally {
      setLoading(false);
    }
  };

  // Effect to save navigation state to sessionStorage
  useEffect(() => {
    const savedState = { page, filters, resultsPerPage };
    sessionStorage.setItem('vehiclesNavState', JSON.stringify(savedState));
  }, [page, filters, resultsPerPage]);

  // Effect to fetch vehicles when dependencies change
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchVehicles();
    }, 150); // Debounce to prevent rapid firing
    return () => clearTimeout(debounceTimer);
  }, [selectedSystem, filters, page, resultsPerPage, sortDirection]);

  // Handler for updating filters that also resets the page
  const updateFilters = (newFilters: Filters) => {
    setPage(1);
    setFilters(newFilters);
  };

  // Return all the state and handlers the component will need
  return {
    vehicles,
    totalVehicles,
    loading,
    page,
    setPage,
    resultsPerPage,
    setResultsPerPage,
    sortDirection,
    setSortDirection,
    filters,
    updateFilters,
    refetch: fetchVehicles, // Expose a manual refetch function
    selectedVehicles,
    setSelectedVehicles
  };
};